import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RiMenu2Line, RiCloseLine, RiHistoryLine } from 'react-icons/ri';
import QuitConfirm from '../../components/shared/QuitConfirm';
import AppSidebar from '../../components/app/AppSidebar';
import { useAuth } from '../../context/AuthContext';
import { getPracticeTest, submitTest } from '../../api/practiceTests.api';
import './PracticeTestTake.css';
import { celebrate } from '../../components/brand/celebrate';

const PracticeTestTake = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();

  /* The test runs outside AppShell so nothing can distract from the clock,
     but a learner still needs a way out to the rest of the app — the topbar
     burger opens the same sidebar as a drawer. */
  const [navOpen, setNavOpen] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  /* Shares AppShell's key so the rail is in the state you left it in when
     you enter a test, and stays that way when you leave. */
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('kitsu-app-sidebar-collapsed') === 'true'; }
    catch (_) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('kitsu-app-sidebar-collapsed', String(navCollapsed)); }
    catch (_) {}
  }, [navCollapsed]);

  // Test data
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Test state
  const [currentSection, setCurrentSection] = useState('vocabulary');
  const [answers, setAnswers] = useState({}); // { sectionName: { questionId: answerIndex } }
  const [timers, setTimers] = useState({}); // { sectionName: secondsRemaining }

  // UI state
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* A part-finished attempt found in storage. While this is set the test is
     not started: nothing ticks and nothing is written back, so the saved
     progress survives until the learner actually chooses what to do with it. */
  const [pendingResume, setPendingResume] = useState(null);

  const timerIntervals = useRef({});

  useEffect(() => {
    loadTest();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const startFresh = useCallback(() => {
    const fresh = {};
    (test?.sections || []).forEach(section => { fresh[section.name] = section.duration * 60; });
    setTimers(fresh);
    setAnswers({});
    setCurrentSection(test?.sections?.[0]?.name || 'vocabulary');
  }, [test]);

  useEffect(() => {
    if (!test) return;

    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(`test_${testId}`)); } catch (_) {}

    /* Only worth asking about if they actually got somewhere — an attempt
       opened and abandoned in the first second is not worth a dialog. */
    const answered = Object.values(saved?.answers || {})
      .reduce((n, sec) => n + Object.keys(sec || {}).length, 0);
    const timeUsed = test.sections.some(sec =>
      (saved?.timers?.[sec.name] ?? sec.duration * 60) < sec.duration * 60);

    if (saved && (answered > 0 || timeUsed)) {
      setPendingResume({ ...saved, answered });
    } else {
      startFresh();
    }

    const intervals = timerIntervals.current;
    return () => {
      // Cleanup timers
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, [test, testId, startFresh]);


  // Save state to localStorage
  useEffect(() => {
    /* Never overwrite a saved attempt the learner has not decided about
       yet, and never write the empty object that exists for one render
       between the test arriving and startFresh() applying. */
    if (test && !pendingResume && Object.keys(timers).length) {
      localStorage.setItem(
        `test_${testId}`,
        JSON.stringify({ answers, timers, currentSection })
      );
    }
  }, [answers, timers, currentSection, test, testId, pendingResume]);

  // Timer effect — only the section you are on counts down, which is how
  // the real exam works. Paused entirely while the resume dialog is up.
  useEffect(() => {
    if (pendingResume) return;
    if (!currentSection || !timers[currentSection]) return;

    timerIntervals.current[currentSection] = setInterval(() => {
      setTimers(prev => {
        const newTime = Math.max(0, prev[currentSection] - 1);
        if (newTime === 0) {
          clearInterval(timerIntervals.current[currentSection]);
        }
        return { ...prev, [currentSection]: newTime };
      });
    }, 1000);

    const intervals = timerIntervals.current;
    return () => {
      if (intervals[currentSection]) {
        clearInterval(intervals[currentSection]);
      }
    };
  }, [currentSection, timers, pendingResume]);

  const loadTest = async () => {
    try {
      setLoading(true);
      const response = await getPracticeTest(testId);
      setTest(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (questionId, optionIndex) => {
    setAnswers(prev => ({
      ...prev,
      [currentSection]: {
        ...(prev[currentSection] || {}),
        [questionId]: optionIndex,
      },
    }));
  };

  const handleSwitchSection = (sectionName) => {
    setCurrentSection(sectionName);
  };

  const handleSubmitTest = async () => {
    try {
      setSubmitting(true);
      const payload = {
        answers,
        timeTaken: timers,
      };
      const response = await submitTest(testId, payload);
      celebrate();
      
      // Clear localStorage
      localStorage.removeItem(`test_${testId}`);
      
      // Navigate to results
      navigate(`/practice-tests/results/${response.data._id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExitTest = () => setConfirmExit(true);

  /* The sidebar's links and Logout would otherwise walk straight out of a
     running test with no warning — the same silent-abandon the ✕ used to
     have. Caught here rather than in AppSidebar so the sidebar stays a
     dumb nav component everywhere else. */
  const [pendingNav, setPendingNav] = useState(null);

  const guardNav = e => {
    const link = e.target.closest('a.nav-item');
    const logout = e.target.closest('[data-nav="logout"]');
    if (!link && !logout) return;          // collapse / close / hover — let through
    e.preventDefault();
    e.stopPropagation();
    setNavOpen(false);
    setPendingNav(logout ? { logout: true } : { to: link.getAttribute('href') });
  };

  const confirmNav = () => {
    const target = pendingNav;
    setPendingNav(null);
    if (target?.logout) { logout(); navigate('/'); }
    else if (target?.to) navigate(target.to);
  };

  if (loading) return <div className="loading">Loading test...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!test) return <div className="error-message">Test not found</div>;

  const section = test.sections.find(s => s.name === currentSection);
  const sectionAnswers = answers[currentSection] || {};

  return (
    <div className="practice-test-take">
      {/* Below 1024px the sidebar is a drawer and this is its scrim; above
          that the media query leaves it inert, which is correct — there the
          rail is simply always on screen. */}
      <div
        className={`sidebar-overlay ${navOpen ? 'visible' : ''}`}
        onClick={() => setNavOpen(false)}
      />

      <div
        className="app-sidebar-wrapper"
        onClickCapture={guardNav}
        style={{
          width: navCollapsed ? 68 : 252,
          flexShrink: 0,
          transition: 'width 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <AppSidebar
          onLogout={logout}
          isOpen={navOpen}
          onClose={() => setNavOpen(false)}
          collapsed={navCollapsed}
          onToggleCollapse={() => setNavCollapsed(v => !v)}
        />
      </div>

      <div className="test-column">
      {/* Header — everything that used to be split between a top bar and a
          bottom bar now lives here, so the questions get the whole screen. */}
      <div className="test-header">
        <button
          className="test-nav-toggle"
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
        >
          <RiMenu2Line size={20} />
        </button>

        <div className="test-info">
          <h2>{test.title}</h2>
          <span className="level-badge">{test.level}</span>
        </div>

        <div className="test-header-actions">
          <button
            className="btn-submit"
            onClick={() => setShowConfirmSubmit(true)}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Test'}
          </button>
          <button className="btn-exit" onClick={handleExitTest} aria-label="Exit test">
            <RiCloseLine size={18} />
          </button>
        </div>
      </div>



      {/* Section Tabs */}
      <div className="section-tabs">
        {test.sections.map(sec => {
          const sectionTime = timers[sec.name] || 0;
          const minutes = Math.floor(sectionTime / 60);
          const seconds = sectionTime % 60;
          const isActive = sec.name === currentSection;

          return (
            <button
              key={sec.name}
              className={`tab ${isActive ? 'active' : ''}`}
              onClick={() => handleSwitchSection(sec.name)}
            >
              <span className="tab-name">{sec.displayName}</span>
              <span className={`tab-time ${sectionTime < 300 ? 'warning' : ''}`}>
                {minutes}:{seconds.toString().padStart(2, '0')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Questions Container */}
      <div className="questions-container">
        <div className="questions-paper">
          <h3>{section?.displayName}</h3>

          {section?.name === 'listening' && section?.audioUrl && (
            <div className="audio-player">
              <audio controls>
                <source src={section.audioUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          <div className="questions-list">
            {section?.questions?.map((question, index) => (
              <div key={question._id} className="question-block">
                <div className="question-number">Question {index + 1}</div>

                {question.imageUrl && (
                  <div className="question-image">
                    <img src={question.imageUrl} alt={`Question ${index + 1}`} />
                  </div>
                )}

                <div className="question-text">{question.question}</div>

                <div className="options">
                  {question.options?.map((option, optionIndex) => (
                    <label
                      key={optionIndex}
                      className={`option ${
                        sectionAnswers[question._id] === optionIndex ? 'selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question_${question._id}`}
                        value={optionIndex}
                        checked={sectionAnswers[question._id] === optionIndex}
                        onChange={() => handleSelectAnswer(question._id, optionIndex)}
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      </div>

      {/* Resume or restart. Shown before anything starts ticking, so the
          decision costs no exam time either way. */}
      {pendingResume && (
        <div className="modal-overlay">
          <div className="modal-content resume-box" onClick={e => e.stopPropagation()}>
            <div className="resume-icon"><RiHistoryLine size={24} /></div>
            <h3>Pick up where you left off?</h3>
            <p>
              You have an unfinished attempt at this test
              {pendingResume.answered > 0
                ? ` with ${pendingResume.answered} question${pendingResume.answered === 1 ? '' : 's'} answered`
                : ''}.
            </p>

            <ul className="resume-times">
              {test.sections.map(sec => {
                const left = pendingResume.timers?.[sec.name] ?? sec.duration * 60;
                const m = Math.floor(left / 60);
                const ss = String(left % 60).padStart(2, '0');
                return (
                  <li key={sec.name}>
                    <span>{sec.displayName}</span>
                    <em>{m}:{ss} left</em>
                  </li>
                );
              })}
            </ul>

            <div className="modal-buttons">
              <button
                className="btn-cancel"
                onClick={() => {
                  localStorage.removeItem(`test_${testId}`);
                  startFresh();
                  setPendingResume(null);
                }}
              >
                Start over
              </button>
              <button
                className="btn-submit"
                onClick={() => {
                  setAnswers(pendingResume.answers || {});
                  setTimers(pendingResume.timers || {});
                  setCurrentSection(pendingResume.currentSection || test.sections[0]?.name);
                  setPendingResume(null);
                }}
              >
                Continue test
              </button>
            </div>
          </div>
        </div>
      )}

      <QuitConfirm
        open={!!pendingNav}
        title="Leave this test?"
        message={pendingNav?.logout
          ? 'Logging out will end this test. Your answers so far are saved.'
          : 'Your answers so far are saved, but the section timers keep running.'}
        confirmLabel={pendingNav?.logout ? 'Log out' : 'Leave test'}
        cancelLabel="Keep going"
        onConfirm={confirmNav}
        onCancel={() => setPendingNav(null)}
      />

      <QuitConfirm
        open={confirmExit}
        title="Leave this test?"
        message="Your answers so far are saved, but the section timers keep running."
        confirmLabel="Leave test"
        cancelLabel="Keep going"
        onConfirm={() => navigate('/practice-tests')}
        onCancel={() => setConfirmExit(false)}
      />

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div className="modal-overlay" onClick={() => setShowConfirmSubmit(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Submit Test?</h3>
            <p>Are you sure you want to submit? You cannot change your answers after submission.</p>
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={() => setShowConfirmSubmit(false)}>
                Continue Test
              </button>
              <button className="btn-submit" onClick={handleSubmitTest}>
                Submit Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeTestTake;
